import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Clock,
  ExternalLink,
  Loader,
  SearchCheck,
} from "lucide-react";
import { Fragment } from "react";
import { toast } from "sonner";

import { EnvironmentBadge } from "@/components/app-registration-badges";
import { ScopeList, ScopeSummary } from "@/components/app-registration-scope-list";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type SignInConsentItem, useCheckSignInApproval } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { formatDate, formatDateTime, formatRelativeTime } from "@/lib/format";
import { logger } from "@/lib/logger";

/** Green is the only success colour the badge palette has no variant for. */
const APPROVED_CLASSES = "bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300";

/**
 * How a consent came to be recorded, in words.
 *
 * The three machine values mirror `CONSENTED_BY_REDIRECT`,
 * `CONSENTED_BY_GRANT_READ` and `CONSENTED_BY_POLICY` in
 * `services/project_sso.py`; anything else is the email of the admin who
 * marked it approved by hand.
 */
function consentedByLabel(consentedBy: string | null | undefined): string | null {
  if (!consentedBy) {
    return null;
  }
  if (consentedBy === "entra:admin-consent-redirect") {
    return "recorded when Entra sent the admin back";
  }
  if (consentedBy === "graph:permission-grant") {
    return "confirmed from the grant in the directory";
  }
  if (consentedBy === "policy:scaffoldui-signin-basic") {
    return "granted automatically when it was created (sign-in scopes only)";
  }
  return `marked approved by ${consentedBy}`;
}

/**
 * Who reported a `reported` consent: the Scaffold UI user whose browser Entra
 * sent back, or — signed out — only that the redirect came.
 */
function reportedByLabel(consentedBy: string | null | undefined): string {
  if (!consentedBy || consentedBy === "entra:admin-consent-redirect") {
    return "reported by Entra's redirect";
  }
  return `reported by ${consentedBy}`;
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs tracking-wider uppercase">{label}</p>
      <div className="text-foreground mt-1 text-sm break-words">{children}</div>
    </div>
  );
}

/**
 * Where IT's consent stands. `pending` after a `consented_at` means permissions
 * were added since; `reported` means Entra said so but the grant could not be read,
 * or did not show yet.
 */
function ConsentStateCell({ item }: { item: SignInConsentItem }) {
  if (item.consent_state === "reported") {
    return (
      <div className="space-y-1">
        <Badge variant="secondary">
          <CircleHelp />
          Reported, unverified
        </Badge>
        <p className="text-muted-foreground text-xs">
          {reportedByLabel(item.consented_by)} on {formatDate(item.consented_at ?? null)}
        </p>
      </div>
    );
  }
  if (item.consent_state === "consented") {
    return (
      <div className="space-y-1">
        <Badge className={APPROVED_CLASSES}>
          <Check />
          Approved
        </Badge>
        <p className="text-muted-foreground text-xs">{formatDate(item.consented_at ?? null)}</p>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      <Badge variant="warning">
        <Clock />
        Awaiting IT approval
      </Badge>
      {item.consented_at && (
        <p className="text-muted-foreground text-xs">
          Permissions added since it was approved on {formatDate(item.consented_at)}
        </p>
      )}
    </div>
  );
}

/**
 * Read the grant from Graph now; the server records the consent itself when it
 * is there. A fallback: the server makes the same read every few minutes.
 */
function CheckButton({ item }: { item: SignInConsentItem }) {
  const check = useCheckSignInApproval();

  const handleCheck = async () => {
    try {
      const result = await check.mutateAsync({
        requestId: item.request_id,
        projectId: item.project_id,
      });
      logger.debug("[it-approvals] check result:", item.request_id, result?.grant);
      if (result?.grant === "granted") {
        toast.success(`Consent found for ${item.display_name} — recorded as approved by IT.`);
      } else if (result?.grant === "not_granted") {
        toast.info(`No consent found yet for ${item.display_name}.`);
      } else {
        // `unknown` is never an approval: Scaffold UI may not be allowed to
        // read grants, so the decision goes back to a human.
        toast.warning(
          "Scaffold UI isn't allowed to read consent. If IT has approved it, mark it approved.",
        );
      }
    } catch (err) {
      logger.error("[it-approvals] check failed:", err);
      toast.error(readApiErrorMessage(err, "Couldn't check consent. Please try again."));
    }
  };

  return (
    <Button
      size="xs"
      variant="ghost"
      className="text-muted-foreground"
      onClick={() => void handleCheck()}
      disabled={check.isPending}
    >
      {check.isPending ? <Loader className="animate-spin" /> : <SearchCheck />}
      {check.isPending ? "Checking…" : "Check"}
    </Button>
  );
}

interface RowActionsProps {
  item: SignInConsentItem;
  onMarkApproved: (item: SignInConsentItem) => void;
}

/**
 * The way out of the queue is the consent link; the server then confirms the
 * approval by itself. Check and Mark approved stay small: they are fallbacks.
 */
function RowActions({ item, onMarkApproved }: RowActionsProps) {
  if (item.consent_state === "consented") {
    return <span className="text-muted-foreground text-xs">Nothing to do</span>;
  }
  return (
    <div className="flex flex-col items-end gap-1">
      {item.admin_consent_link ? (
        <Button size="sm" asChild>
          <a href={item.admin_consent_link} target="_blank" rel="noopener noreferrer">
            Open admin consent
            <ExternalLink />
          </a>
        </Button>
      ) : (
        <Button size="sm" disabled aria-describedby={`no-link-${item.request_id}`}>
          Open admin consent
        </Button>
      )}
      <div className="flex flex-wrap justify-end gap-0.5">
        <CheckButton item={item} />
        <Button
          size="xs"
          variant="ghost"
          className="text-muted-foreground"
          onClick={() => onMarkApproved(item)}
        >
          Mark approved
        </Button>
      </div>
      {!item.admin_consent_link && (
        // `null` when this instance's address cannot be a redirect URI, so
        // Entra would have nowhere to send the admin back to.
        <p
          id={`no-link-${item.request_id}`}
          className="text-muted-foreground max-w-xs text-right text-xs"
        >
          No consent link: Entra can&apos;t send the admin back to this Scaffold UI address. Grant
          consent in the Entra portal; this page picks it up within a few minutes. If it
          doesn&apos;t, an admin can mark it approved.
        </p>
      )}
    </div>
  );
}

/** What does not fit the collapsed row: each permission IT consents to, and the history. */
function ApprovalRowDetail({ item }: { item: SignInConsentItem }) {
  const consentedBy = consentedByLabel(item.consented_by);
  return (
    <div className="bg-muted/30 space-y-5 px-4 py-4">
      <div className="grid gap-5 sm:grid-cols-2">
        <DetailField label="Created">{formatDateTime(item.created_at)}</DetailField>
        <DetailField label="IT approval">
          {item.consent_state === "reported" ? (
            <>
              Reported {formatDateTime(item.consented_at ?? null)} &mdash;{" "}
              {reportedByLabel(item.consented_by)}
              <p className="text-muted-foreground mt-1 text-xs">
                Entra sent the admin back saying consent was granted, but the grant didn&apos;t show
                yet. Scaffold UI keeps checking and confirms it by itself, normally within a few
                minutes. If it stays reported, Scaffold UI may not be allowed to read grants: mark
                it approved once you have seen it in the Entra portal.
              </p>
            </>
          ) : item.consented_at ? (
            <>
              {item.consent_state === "consented" ? "Approved" : "Last approved"}{" "}
              {formatDateTime(item.consented_at)}
              {consentedBy ? ` — ${consentedBy}` : ""}
              {item.consent_state === "pending" && (
                <p className="text-muted-foreground mt-1 text-xs">
                  Permissions were added since, and Entra does not extend an existing consent, so it
                  needs approving again. Sign-in keeps working meanwhile.
                </p>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">
              Never approved &mdash; nobody can sign in to this environment yet.
            </span>
          )}
        </DetailField>
        {item.portal_url && (
          <DetailField label="Entra portal">
            <a
              href={item.portal_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary inline-flex items-center gap-1 underline-offset-2 hover:underline"
            >
              API permissions <ExternalLink className="size-3" />
            </a>
            <p className="text-muted-foreground mt-1 text-xs">
              The same consent, granted by hand. This page picks it up within a few minutes; if it
              doesn&apos;t, an admin can mark it approved.
            </p>
          </DetailField>
        )}
      </div>

      <div>
        <p className="text-muted-foreground text-xs tracking-wider uppercase">
          Permissions IT consents to
        </p>
        <ScopeList scopeKeys={item.scope_keys ?? []} className="mt-2" />
      </div>
    </div>
  );
}

interface ApprovalTableProps {
  items: SignInConsentItem[];
  /** Whether consented rows are listed too — only changes the empty state. */
  includeConsented: boolean;
  expandedId: string | null;
  onToggleExpanded: (requestId: string) => void;
  onMarkApproved: (item: SignInConsentItem) => void;
}

export function ApprovalTable({
  items,
  includeConsented,
  expandedId,
  onToggleExpanded,
  onMarkApproved,
}: ApprovalTableProps) {
  if (items.length === 0) {
    return (
      <div className="text-muted-foreground py-12 text-center text-sm">
        {includeConsented
          ? "No project has its own sign-in app registration yet."
          : "Nothing is waiting for IT approval."}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Project</TableHead>
          <TableHead>Environment</TableHead>
          <TableHead>App registration</TableHead>
          <TableHead>Requester</TableHead>
          <TableHead>Permissions</TableHead>
          <TableHead>State</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => {
          const expanded = expandedId === item.request_id;
          return (
            <Fragment key={item.request_id}>
              <TableRow>
                <TableCell className="text-muted-foreground align-top">
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => onToggleExpanded(item.request_id)}
                    className="hover:text-foreground"
                  >
                    {expanded ? (
                      <ChevronDown className="size-4" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                    <span className="sr-only">
                      {expanded ? "Collapse" : "Expand"} {item.display_name}
                    </span>
                  </button>
                </TableCell>
                <TableCell className="align-top">
                  <p className="text-foreground text-sm font-medium">{item.project_name}</p>
                </TableCell>
                <TableCell className="align-top">
                  <EnvironmentBadge environment={item.environment} />
                </TableCell>
                <TableCell className="align-top">
                  <p className="text-foreground font-mono text-xs font-medium">
                    {item.display_name}
                  </p>
                  <p
                    className="text-muted-foreground font-mono text-xs break-all"
                    title="Application (client) ID"
                  >
                    {item.entra_app_id}
                  </p>
                </TableCell>
                <TableCell className="align-top">
                  <p className="text-foreground text-sm">{item.requester_email}</p>
                  <p className="text-muted-foreground text-xs">
                    {formatRelativeTime(item.created_at)}
                  </p>
                </TableCell>
                <TableCell className="align-top">
                  <ScopeSummary scopeKeys={item.scope_keys ?? []} />
                </TableCell>
                {/* Both carry a sentence now and then; the table defaults to nowrap. */}
                <TableCell className="max-w-48 align-top whitespace-normal">
                  <ConsentStateCell item={item} />
                </TableCell>
                <TableCell className="align-top whitespace-normal">
                  <RowActions item={item} onMarkApproved={onMarkApproved} />
                </TableCell>
              </TableRow>
              {expanded && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={8} className="p-0 whitespace-normal">
                    <ApprovalRowDetail item={item} />
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
}
